import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import { Button, Grid } from '@material-ui/core';
import { Visibility } from '@material-ui/icons';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import HoveredCard from '../../common/card/HoveredCard';
import DeleteEntityCard from '../../common/cards/DeleteEntityCard';
import CartIconBadge from '../../common/cart/CartIconBadge';
import { formatDate } from '../../common/formatter';
import HeaderBackButton, {
  StyledPageTemplate
} from '../../common/HeaderBackButton';
import HeaderTitle from '../../common/HeaderTitle';
import PageTemplateAlert from '../../common/page-template/PageTemplateAlert';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import environment from '../../environments';
import { useUserContext } from '../../user/User.context';
import { useJiraConnectAPI } from '../jira-connect/JiraConnect.hooks';
import { JiraServerContext } from '../jira-server/jira-server.context';
import SubTaskListCard from '../tasks/cards/SubTaskListCard';
import TaskAssignCard from '../tasks/cards/TaskAssignCard';
import TaskAttachmentCard from '../tasks/cards/TaskAttachmentCard';
import TaskFileListCard from '../tasks/cards/TaskFileListCard';
import TaskStatusVersionCard from '../tasks/cards/TaskStatusVersionCard';
import { Task } from '../tasks/Task.interface';
import TaskStatusActions from '../tasks/TaskStatusActions';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';
import { ProductBoardColumnEntity } from './board/ProductBoardColumn';
import TaskJiraIssueAssignCard from '../tasks/cards/TaskJiraIssueAssignCard';
import ProductsSubSectionComponentCard from './cards/ProductsSubSectionComponentCard';
import ProductsSubSectionReleaseCard from './cards/ProductsSubSectionReleaseCard';
import TestStepCard from './cards/TestStepCard';
import ValidationDesignInputReferenceCard from './cards/ValidationDesignInputReferenceCard';
import ValidationInfoCard from './cards/ValidationInfoCard';
import ValidationUserNeedReferenceCard from './cards/ValidationUserNeedReferenceCard';
import {
  getProductEntityName,
  isProductRelationDisabled
} from './Product.config';
import {
  useFindProductValidationById,
  useFindProductValidationVersion,
  useProductEntityPrefixId
} from './Product.hooks';
import {
  BaseProductValidationCardProps,
  ProductValidation
} from './Product.interface';
import TaskDueDateCard from '../tasks/cards/TaskDueDateCard';

const ProductValidationDetailPage: React.FC = () => {
  const {
    productId: paramProductId = '',
    validationId: paramValidationId = ''
  } = useParams<{ productId: string; validationId: string }>();
  const productId: number = parseInt(paramProductId) || 0;
  const validationId: number = parseInt(paramValidationId) || 0;
  const { hasRole } = useUserContext();
  const [from = 'detail'] = useQueryParam('from', StringParam);
  const [version] = useQueryParam('ver', withDefault(NumberParam, undefined));
  const [productValidation, setProductValidation] = useState<
    ProductValidation | undefined
  >();
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const { isAvailable: isJiraConnect } = useJiraConnectAPI();
  const { isJiraServer } = useContext(JiraServerContext);
  const {
    productValidation: data,
    isLoadingProductValidation,
    findProductValidationError,
    findProductValidation
  } = useFindProductValidationById(productId, validationId);
  const {
    productValidationVersion: loadedProductValidationVersion,
    findProductValidationVersion
  } = useFindProductValidationVersion(productId, validationId, version);
  const [productValidationVersion, setProductValidationVersion] = useState(
    loadedProductValidationVersion
  );

  const title: string = useProductEntityPrefixId(
    productValidation,
    ProductBoardColumnEntity.Validation
  );
  const { isFieldDisabled } = useTaskEntityFields(productValidation?.task);

  const cardProps: BaseProductValidationCardProps = {
    productId,
    productValidation: productValidation!,
    productValidationVersion,
    onChange: (newProductValidation: Partial<ProductValidation>) => {
      if (productValidation) {
        // notify task components to refresh
        productValidation.task = { ...productValidation.task };
        setProductValidation({
          ...productValidation,
          ...newProductValidation
        });
      }
    },
    onUpdate: () => setDidUpdate(true),
    onError: (error: FetchError) => setError(error)
  };

  const handleTaskUpdate = (newTask: Partial<Task>) => {
    if (!productValidation?.task) {
      return;
    }
    Object.assign(productValidation.task, newTask);
    cardProps.onChange?.({});
    cardProps.onUpdate?.();
  };

  useEffect(() => {
    findProductValidation();
  }, [findProductValidation, productId, validationId]);
  useEffect(() => {
    if (version) {
      findProductValidationVersion();
    } else {
      setProductValidationVersion(undefined);
    }
  }, [findProductValidationVersion, productId, validationId, version]);
  useEffect(
    () =>
      loadedProductValidationVersion &&
      setProductValidationVersion(loadedProductValidationVersion),
    [loadedProductValidationVersion]
  );

  useEffect(() => {
    data && setProductValidation(data);
  }, [data]);

  useEffect(() => {
    setError(findProductValidationError);
  }, [findProductValidationError]);
  const task = productValidationVersion?.task || productValidation?.task;
  return (
    <StyledPageTemplate>
      <PageTemplateHeader>
        <HeaderBackButton to={`/portal/products/${productId}/${from}`} />
        <HeaderTitle>
          <h2>{isLoadingProductValidation ? 'Loading...' : title}</h2>
          {productValidation && (
            <div className='status-container'>
              <TaskStatusLabel task={productValidation.task} />
            </div>
          )}
        </HeaderTitle>
        <PageTemplateHeaderActions flex>
          <CartIconBadge
            taskEntity={productValidation}
            onChange={productValidation =>
              setProductValidation(productValidation as ProductValidation)
            }
          />
          {productValidation?.task && (
            <TaskStatusActions
              task={productValidation.task}
              taskEntity={productValidation}
              onChange={(task: Task) => {
                productValidation.task = task;
                setProductValidation({ ...productValidation });
              }}
            />
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      {productValidation && version && (
        <PageTemplateAlert severity='warning'>
          Viewing previous version {version}
          {productValidationVersion &&
            ` from ${formatDate(productValidationVersion.created)}. `}
          <Link to={`./${productValidation.id}`}>
            Click here to view latest version
          </Link>
        </PageTemplateAlert>
      )}
      <PageTemplateDashboard>
        {productValidation && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <ValidationInfoCard {...cardProps} />
                </Grid>
                {!environment.productConfig?.verification?.hideTestSteps && (
                  <Grid item xs={12}>
                    <TestStepCard
                      validationId={validationId}
                      onError={cardProps.onError}
                      onUpdate={cardProps.onUpdate}
                      disabled={
                        Boolean(productValidationVersion) ||
                        isFieldDisabled('testScriptEditor')
                      }
                    />
                  </Grid>
                )}
                {(isJiraConnect || isJiraServer) && (
                  <Grid item xs={12}>
                    <TaskJiraIssueAssignCard
                      task={productValidation.task}
                      entity={{
                        title: productValidation.title,
                        description: productValidation.description
                      }}
                      jiraFieldColumns={
                        environment.productConfig?.validation?.visibleJiraFields
                      }
                      onChange={taskJiraIssues => {
                        // update productValidation Task jira issues on change
                        productValidation.task.jiraIssues = taskJiraIssues;
                        setProductValidation({ ...productValidation });
                      }}
                    />
                  </Grid>
                )}
                {productValidation.task && (
                  <Grid item xs={12}>
                    <TaskAttachmentCard
                      task={productValidation.task}
                      disabled={Boolean(productValidationVersion)}
                      attachments={productValidationVersion?.attachments}
                      onError={cardProps.onError}
                      onChange={(newTask: Partial<Task>) => {
                        Object.assign(productValidation.task, newTask);
                        cardProps.onChange?.({});
                        cardProps.onUpdate?.();
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={3}>
                {productValidation.task && (
                  <>
                    <Grid item xs={12}>
                      <TaskStatusVersionCard
                        showCart
                        taskEntity={productValidation}
                        renderContent={group =>
                          group.version !== productValidation.task.version && (
                            <HoveredCard
                              title={`Version ${group.version}`}
                              content='View the previous version'
                              actions={
                                <Button
                                  variant='text'
                                  color='primary'
                                  component={Link}
                                  startIcon={<Visibility />}
                                  to={`./${productValidation.id}?ver=${group.version}`}
                                >
                                  View
                                </Button>
                              }
                            />
                          )
                        }
                      />
                    </Grid>
                    {task?.children && task.children.length > 0 && (
                      <Grid item xs={12}>
                        <SubTaskListCard
                          title='Stakeholders'
                          task={task}
                          disabled={
                            Boolean(version) ||
                            isFieldDisabled('assignSubTasks')
                          }
                          onError={cardProps.onError}
                          onChange={handleTaskUpdate}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <ProductsSubSectionComponentCard
                        productSubSection={productValidation}
                        productId={productId}
                        readOnlyComponents={
                          productValidationVersion &&
                          (productValidationVersion.components || [])
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ProductsSubSectionReleaseCard
                        productId={productId}
                        productSubSection={productValidation}
                        disabled={Boolean(productValidationVersion)}
                        readOnlyRelease={
                          productValidationVersion &&
                          (productValidationVersion?.release || null)
                        }
                      />
                    </Grid>
                    {task && (
                      <Grid item xs={12}>
                        <TaskAssignCard task={task} />
                      </Grid>
                    )}
                    {productValidation.task && (
                      <>
                        <Grid item xs={12}>
                          <TaskFileListCard
                            task={productValidation.task}
                            disabled={Boolean(productValidationVersion)}
                            onError={cardProps.onError}
                            onChange={handleTaskUpdate}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TaskDueDateCard
                            task={productValidation.task}
                            disabled={Boolean(productValidationVersion)}
                            onError={cardProps.onError}
                            onChange={handleTaskUpdate}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}
                {!isProductRelationDisabled('validation', 'userNeeds') && (
                  <Grid item xs={12}>
                    <ValidationUserNeedReferenceCard {...cardProps} />
                  </Grid>
                )}
                {!isProductRelationDisabled('validation', 'designInputs') && (
                  <Grid item xs={12}>
                    <ValidationDesignInputReferenceCard {...cardProps} />
                  </Grid>
                )}
                {hasRole('Delete') && (
                  <Grid item xs={12}>
                    <DeleteEntityCard
                      title={getProductEntityName('validation')}
                      apiPath={`products/${productId}/validations`}
                      entity={productValidation}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </PageTemplateDashboard>

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Product Validation details updated.
      </AppSnackbar>
    </StyledPageTemplate>
  );
};

export default ProductValidationDetailPage;
